/** Pure draft mutators — the SINGLE place pull-plan staged ops are edited.
 *
 *  Every grid edit funnels through one of these functions, which take the
 *  current DraftPayload and return the next one. Centralizing them guarantees
 *  the cross-references between arrays stay coherent: deleting an activity also
 *  drops the dep-edits naming it; removing a zone drops the retags touching it;
 *  re-zoning merges per-activity instead of wholesale-replacing. The grid never
 *  hand-edits a pending_* array inline (which is how arrays drifted out of sync).
 *
 *  No React, no IO — unit-testable directly.
 */
import type {
  DepEditOp,
  DraftPayload,
  PendingNewActivity,
  PendingTaktification,
  PendingWorkCodeAdd,
  PendingWorkCodeEdit,
  ZoneTuple,
} from "../../../models";
import type { ActivityInstance, ZoneRow } from "./types";
import { sameZone, zoneKey, zoneOf } from "./zones";

/** Does an edge-op endpoint ref name one of the removed activities? A draft_id
 *  is a string; a committed id is a number (string refs may also stringify a
 *  numeric id, so check both). */
function refRemoved(
  ref: number | string,
  existing: Set<number>,
  drafts: Set<string>,
): boolean {
  if (typeof ref === "string") return drafts.has(ref) || existing.has(Number(ref));
  return existing.has(ref);
}

/** Drop staged edge-ops orphaned by an activity delete, so the deleted activity
 *  can never leave an edge the graph fetch / publish rejects. `add` ops carry
 *  their endpoints directly. `remove`/`update` ops carry only a committed
 *  edge_id; when `edgeEndpoints` (edge_id -> its graph endpoints) is supplied we
 *  resolve and drop those too if an endpoint was deleted. (The backend read path
 *  also tolerates a stale remove/update, so an unresolved one degrades, not
 *  400s.) */
function pruneEdgesReferencing(
  edits: DepEditOp[],
  existing: Set<number>,
  drafts: Set<string>,
  edgeEndpoints?: Map<number, { pred: string | number; succ: string | number }>,
): DepEditOp[] {
  return edits.filter((e) => {
    if (e.op === "add") {
      return !(
        refRemoved(e.pred_activity_id, existing, drafts) ||
        refRemoved(e.succ_activity_id, existing, drafts)
      );
    }
    if ((e.op === "remove" || e.op === "update") && edgeEndpoints) {
      const ep = edgeEndpoints.get(e.edge_id);
      if (
        ep &&
        (refRemoved(ep.pred, existing, drafts) ||
          refRemoved(ep.succ, existing, drafts))
      ) {
        return false;
      }
    }
    return true;
  });
}

/** Stage Auto-Takt / Add-Work-Code output: UPSERT taktifications by activity_id
 *  and work-code adds by code, so a new batch merges in without dropping any
 *  prior staged op. */
export function stageTaktifications(
  draft: DraftPayload,
  taktifications: PendingTaktification[],
  workCodeAdds: PendingWorkCodeAdd[],
): DraftPayload {
  const byId = new Map(draft.pending_taktifications.map((t) => [t.activity_id, t]));
  for (const t of taktifications) byId.set(t.activity_id, t);
  const byCode = new Map(draft.pending_work_code_adds.map((w) => [w.code, w]));
  for (const w of workCodeAdds) byCode.set(w.code, w);
  return {
    ...draft,
    pending_taktifications: Array.from(byId.values()),
    pending_work_code_adds: Array.from(byCode.values()),
  };
}

/** Re-zone already-coded activities (legend pencil / CodeEditModal). UPSERT by
 *  activity_id: only the activities the editor actually re-supplies change; an
 *  untouched activity under the same code keeps its prior staged taktification
 *  instead of being silently un-coded. */
export function stageRezone(
  draft: DraftPayload,
  taktifications: PendingTaktification[],
): DraftPayload {
  const byId = new Map(draft.pending_taktifications.map((t) => [t.activity_id, t]));
  for (const t of taktifications) byId.set(t.activity_id, t);
  return { ...draft, pending_taktifications: Array.from(byId.values()) };
}

/** Append a freshly-pulled (draft-added) activity. */
export function addNewActivity(
  draft: DraftPayload,
  entry: PendingNewActivity,
): DraftPayload {
  return { ...draft, pending_new_activities: [...draft.pending_new_activities, entry] };
}

/** Stage a duration edit for a committed activity. Every duration-edit path
 *  (drag-resize, train apply, ✎, the sequence table) records the value here so
 *  the staged log stays the activity's duration intent. `buildEffectiveDraft`
 *  prefers the live bar geometry when it diverges from baseline, and otherwise
 *  honors this staged value — so an edit on a bar-backed activity still survives
 *  even when its `instances` copy wasn't touched. */
export function stageDurationEdit(
  draft: DraftPayload,
  activityId: number,
  durationDays: number,
): DraftPayload {
  const rest = draft.pending_duration_edits.filter((d) => d.activity_id !== activityId);
  return {
    ...draft,
    pending_duration_edits: [...rest, { activity_id: activityId, duration_days: durationDays }],
  };
}

/** Stage a task_name change for a committed activity. Draft-added activities
 *  patch their pending_new_activities entry instead. UPSERT by activity_id so repeated
 *  renames of the same activity keep one entry. */
export function stageRenameEdit(
  draft: DraftPayload,
  activityId: number,
  name: string,
): DraftPayload {
  const rest = draft.pending_rename_edits.filter((r) => r.activity_id !== activityId);
  return {
    ...draft,
    pending_rename_edits: [...rest, { activity_id: activityId, name }],
  };
}

/** Stage removal of bars. Existing activities become pending deletes and shed
 *  every staged op naming them (taktification, duration edit, rename, add-edges);
 *  draft-added bars drop their new-activity entry and its add-edges. */
export function deleteActivities(
  draft: DraftPayload,
  existingIds: number[],
  draftIds: string[],
  edgeEndpoints?: Map<number, { pred: string | number; succ: string | number }>,
): DraftPayload {
  const existingSet = new Set(existingIds);
  const draftSet = new Set(draftIds);
  const already = new Set(draft.pending_activity_deletes.map((d) => d.activity_id));
  const adds = existingIds
    .filter((id) => !already.has(id))
    .map((id) => ({ activity_id: id }));
  return {
    ...draft,
    pending_activity_deletes: [...draft.pending_activity_deletes, ...adds],
    pending_taktifications: draft.pending_taktifications.filter(
      (t) => !existingSet.has(t.activity_id),
    ),
    pending_duration_edits: draft.pending_duration_edits.filter(
      (d) => !existingSet.has(d.activity_id),
    ),
    pending_rename_edits: draft.pending_rename_edits.filter(
      (r) => !existingSet.has(r.activity_id),
    ),
    pending_new_activities: draft.pending_new_activities.filter(
      (n) => !draftSet.has(n.draft_id),
    ),
    pending_dep_edits: pruneEdgesReferencing(
      draft.pending_dep_edits,
      existingSet,
      draftSet,
      edgeEndpoints,
    ),
  };
}

/** Rename a zone row: relabel the whole zone (old → new tuple). Retag chains
 *  collapse (A→B→C stages one A→C; a rename back to origin cancels), and every
 *  staged op keyed on the old zone follows the rename so the draft stays
 *  coherent. `newDraftIds` are the draft-added bars currently in the row (their
 *  new-activity entries re-zone directly, even if their stale tuple differs). */
export function renameZone(
  draft: DraftPayload,
  oldZone: ZoneTuple,
  newZone: ZoneTuple,
  newDraftIds: Set<string>,
): DraftPayload {
  const chained = draft.pending_zone_retags.find((r) => sameZone(r.new, oldZone));
  const retags = chained
    ? draft.pending_zone_retags
        .map((r) => (r === chained ? { ...r, new: newZone } : r))
        .filter((r) => !sameZone(r.old, r.new))
    : [...draft.pending_zone_retags, { old: oldZone, new: newZone }];
  const relabel = <T extends { takt_a?: string | null; takt_b?: string | null; takt_c?: string | null }>(
    x: T,
  ): T => ({
    ...x,
    takt_a: newZone.takt_a || null,
    takt_b: newZone.takt_b || null,
    takt_c: newZone.takt_c || null,
  });
  return {
    ...draft,
    pending_zone_retags: retags,
    pending_taktifications: draft.pending_taktifications.map((t) =>
      sameZone(zoneOf(t), oldZone) ? relabel(t) : t,
    ),
    pending_new_activities: draft.pending_new_activities.map((n) =>
      newDraftIds.has(n.draft_id) || sameZone(zoneOf(n), oldZone) ? relabel(n) : n,
    ),
    pending_untaktifications: draft.pending_untaktifications.map((z) =>
      sameZone(z, oldZone) ? newZone : z,
    ),
  };
}

/** Delete a zone row: UNTAKT its activities (strip placement, keep work code +
 *  section) so they return to the palette — does NOT hard-delete. Staged by zone
 *  tuple so publish also catches off-screen members (completed work). Drops the
 *  zone's draft-added bars + any retag touching the zone on EITHER end (so a
 *  retag whose `old` is the removed zone never dangles), plus add-edges naming
 *  the removed draft bars. `draftOnly` zones have nothing committed to untakt. */
export function removeZoneRow(
  draft: DraftPayload,
  zone: ZoneTuple,
  draftOnly: boolean,
  draftIds: Set<string>,
): DraftPayload {
  return {
    ...draft,
    pending_untaktifications:
      draftOnly || draft.pending_untaktifications.some((z) => sameZone(z, zone))
        ? draft.pending_untaktifications
        : [...draft.pending_untaktifications, zone],
    pending_new_activities: draft.pending_new_activities.filter(
      (n) => !draftIds.has(n.draft_id),
    ),
    pending_taktifications: draft.pending_taktifications.filter(
      (t) => !sameZone(zoneOf(t), zone),
    ),
    pending_zone_retags: draft.pending_zone_retags.filter(
      (r) => !sameZone(r.new, zone) && !sameZone(r.old, zone),
    ),
    pending_dep_edits: pruneEdgesReferencing(
      draft.pending_dep_edits,
      new Set<number>(),
      draftIds,
    ),
  };
}

/** Remove ONE activity from its work code: full-untag it (strip code + zone,
 *  keep section) so it returns to the untagged palette — does NOT delete it.
 *  Sheds the activity's staged taktification / duration edit / add-edges. */
export function removeFromWorkCode(
  draft: DraftPayload,
  activityId: number,
): DraftPayload {
  const already = draft.pending_work_code_removals.some((r) => r.activity_id === activityId);
  const idSet = new Set([activityId]);
  return {
    ...draft,
    pending_work_code_removals: already
      ? draft.pending_work_code_removals
      : [...draft.pending_work_code_removals, { activity_id: activityId }],
    pending_taktifications: draft.pending_taktifications.filter(
      (t) => t.activity_id !== activityId,
    ),
    pending_duration_edits: draft.pending_duration_edits.filter(
      (d) => d.activity_id !== activityId,
    ),
    pending_dep_edits: pruneEdgesReferencing(draft.pending_dep_edits, idSet, new Set<string>()),
  };
}

/** Delete a work code FROM THIS SECTION. Handles a code that is committed,
 *  drafted (in pending_work_code_adds), OR BOTH at once: the drafted side drops
 *  its add + draft activities + staged taktifications, and the committed side
 *  full-untags every committed carrier and hides the chip from the section
 *  legend. `committedCarrierIds` are the section's activities whose COMMITTED
 *  Takt_Work is this code (untagged activities merely staged to it are reverted
 *  by dropping their taktification, not full-untagged). The code lives on in any
 *  other section that uses it. */
export function deleteWorkCode(
  draft: DraftPayload,
  code: string,
  committedCarrierIds: number[],
): DraftPayload {
  // Drafted side: every draft-added activity under this code goes away.
  const draftActIds = new Set(
    draft.pending_new_activities
      .filter((n) => n.work_code === code)
      .map((n) => n.draft_id),
  );
  // Committed side: full-untag the committed carriers + hide the legend chip.
  const already = new Set(draft.pending_work_code_removals.map((r) => r.activity_id));
  const newRemovals = committedCarrierIds
    .filter((id) => !already.has(id))
    .map((id) => ({ activity_id: id }));
  const carrierSet = new Set(committedCarrierIds);
  const hasCommitted = committedCarrierIds.length > 0;
  const suppressed = draft.pending_work_code_deletes.some((d) => d.code === code);
  return {
    ...draft,
    pending_work_code_adds: draft.pending_work_code_adds.filter((w) => w.code !== code),
    pending_new_activities: draft.pending_new_activities.filter((n) => n.work_code !== code),
    pending_work_code_removals: [...draft.pending_work_code_removals, ...newRemovals],
    // A purely-drafted code's chip vanishes when its add is dropped; a committed
    // code needs an explicit legend-hide so the chip leaves before publish.
    pending_work_code_deletes:
      hasCommitted && !suppressed
        ? [...draft.pending_work_code_deletes, { code }]
        : draft.pending_work_code_deletes,
    pending_work_code_edits: draft.pending_work_code_edits.filter((e) => e.code !== code),
    pending_taktifications: draft.pending_taktifications.filter(
      (t) => t.takt_work !== code && !carrierSet.has(t.activity_id),
    ),
    pending_duration_edits: draft.pending_duration_edits.filter(
      (d) => !carrierSet.has(d.activity_id),
    ),
    pending_dep_edits: pruneEdgesReferencing(draft.pending_dep_edits, carrierSet, draftActIds),
  };
}

/** Rename, recolor, and/or re-code a work code for THIS section's carriers (a
 *  section-local fork). UPSERT-merges by the committed `code` so a rename then a
 *  recolor accumulate into one op. `patch.label` is the new display name;
 *  `patch.color` the new color; `patch.newCode` the new Takt_Work value.
 *  A null/undefined label/color leaves the prior staged value intact (it is
 *  "no change", never a wipe). `patch.newCode` is keyed against the committed
 *  `code`: a value equal to `code` (or blank) clears a prior re-code, so the
 *  edit always carries old→new relative to the still-committed code value. */
export function editWorkCode(
  draft: DraftPayload,
  code: string,
  patch: { label?: string | null; color?: string | null; newCode?: string },
): DraftPayload {
  const existing = draft.pending_work_code_edits.find((e) => e.code === code);
  const next: PendingWorkCodeEdit = { ...(existing ?? {}), code };
  if (patch.label != null) next.label = patch.label;
  if (patch.color != null) next.color = patch.color;
  if (patch.newCode !== undefined) {
    const nc = patch.newCode.trim();
    if (nc && nc !== code) next.new_code = nc;
    else delete next.new_code;
  }
  return {
    ...draft,
    pending_work_code_edits: [
      ...draft.pending_work_code_edits.filter((e) => e.code !== code),
      next,
    ],
  };
}

/** The draft exactly as publish sees it: the staged pending_* ops PLUS the three
 *  arrays DERIVED from live board geometry — duration edits on committed bars,
 *  zone order from row order, and new-activity duration/zone reconciled from
 *  their bar. The autosave PUT and the dep-graph preview both consume this one
 *  function, so what you preview is what's saved and committed. */
export function buildEffectiveDraft(
  draft: DraftPayload,
  rows: ZoneRow[],
  instances: ActivityInstance[],
  seedRows: ZoneRow[],
  seedInstances: ActivityInstance[],
  /** Duration baselines for committed bars the seed never carried — scope
   *  members pulled onto the board mid-draft. Keyed by activity id; the value
   *  is the member's committed restart→reend span, so a resize of such a bar
   *  still derives its duration edit. */
  extraDurationBaselines?: Map<number, number>,
): DraftPayload {
  const origDurByAid = new Map<number, number>();
  extraDurationBaselines?.forEach((days, aid) => origDurByAid.set(aid, days));
  for (const i of seedInstances) {
    if (i.source_activity_id != null) origDurByAid.set(i.source_activity_id, i.duration_days);
  }
  // A bar-backed activity's duration comes from the bar when its geometry
  // diverges from baseline (a drag / ✎), otherwise from its staged edit (train
  // apply and drag stage the value without touching the `instances` copy). The
  // non-bar-backed staged edits (unzoned sequence-table members) pass straight
  // through.
  const barBacked = new Set<number>();
  for (const i of instances) {
    if (i.source_activity_id != null) barBacked.add(i.source_activity_id);
  }
  const stagedDur = new Map(
    draft.pending_duration_edits.map((d) => [d.activity_id, d.duration_days]),
  );
  const durationEdits = draft.pending_duration_edits.filter(
    (d) => !barBacked.has(d.activity_id),
  );
  for (const i of instances) {
    if (i.source_activity_id == null) continue;
    const orig = origDurByAid.get(i.source_activity_id);
    if (orig == null) continue;
    // The live bar geometry (instances) wins when a drag / ✎ moved it off the
    // baseline; otherwise a staged edit carries the change. Train apply and
    // drag-resize stage pending_duration_edits WITHOUT touching the instances
    // copy, so honoring the staged value here is what stops an applied duration
    // from reverting on the next recompute.
    const staged = stagedDur.get(i.source_activity_id);
    const live = i.duration_days !== orig ? i.duration_days : staged ?? orig;
    if (live !== orig) {
      durationEdits.push({
        activity_id: i.source_activity_id,
        duration_days: live,
      });
    }
  }

  const seedOrder = seedRows.map((r) => zoneKey(r.takt_a, r.takt_b, r.takt_c)).join(">");
  const currentOrder = rows.map((r) => zoneKey(r.takt_a, r.takt_b, r.takt_c)).join(">");
  const zoneOrder = currentOrder === seedOrder ? [] : rows.map((r) => zoneOf(r));

  const rowById = new Map(rows.map((r) => [r.rowId, r]));
  const instByDraft = new Map<string, ActivityInstance>();
  for (const i of instances) if (i.draftId) instByDraft.set(i.draftId, i);
  const reconciledNew = draft.pending_new_activities.map((n) => {
    const inst = instByDraft.get(n.draft_id);
    if (!inst) return n;
    const row = rowById.get(inst.rowId);
    return {
      ...n,
      duration_days: inst.duration_days,
      takt_a: row ? row.takt_a || null : n.takt_a,
      takt_b: row ? row.takt_b || null : n.takt_b,
      takt_c: row ? row.takt_c || null : n.takt_c,
    };
  });

  return {
    ...draft,
    pending_zone_order: zoneOrder,
    pending_new_activities: reconciledNew,
    pending_duration_edits: durationEdits,
  };
}
